import dayjs from '~/timeInit';

import type { LensCardData, LensFilterValues } from '~/types/leLens';

export const AGENT_HUB_BDM_PAGE_ID = 'agent-hub-bdm';

// Cadence, BDM and coverage segment are Lens filters; the other workbook parameters are applied on the page.
export const CADENCES = ['Daily', 'Weekly', 'Monthly', 'Quarterly', 'Yearly'] as const;
export type Cadence = (typeof CADENCES)[number];
export const COMPARISONS = ['Previous Period', 'Previous Month', 'Previous Quarter', 'Previous Year'] as const;
export type Comparison = (typeof COMPARISONS)[number];
export const ATTRIBUTION_WINDOWS = [7, 14, 30] as const;
export type AttributionWindow = (typeof ATTRIBUTION_WINDOWS)[number];
export const SPLITS = ['BDM Name', 'Agency Booked in Period'] as const;
export type Split = (typeof SPLITS)[number];
export const COVERAGE_SEGMENTS = ['Signed in Period', 'Active - Interacted', 'Active - Not Interacted', 'Inactive'];
const DEFAULT_WINDOW_DAYS = 7;

export type BdmFilters = {
  from: string;
  to: string;
  comparison: Comparison;
  cadence: Cadence;
  attributionWindow: AttributionWindow;
  split: Split;
  bdm: Array<string>;
  segments: Array<string>;
};

export type CardResult = { rows: Array<Record<string, unknown>>; truncated: boolean; card: LensCardData };
export type CardProblem = { problem: string };

const ISO = 'YYYY-MM-DD';
const sydneyDay = (date: Date) => new Intl.DateTimeFormat('en-CA', { timeZone: 'Australia/Sydney' }).format(date);
const addDays = (day: string, amount: number) => dayjs(day).add(amount, 'day').format(ISO);
/** The last complete Sydney day; both tables are rebuilt overnight. */
const lastCompleteDay = (now = new Date()) => addDays(sydneyDay(now), -1);

/** Tableau opens on the last seven complete days, compared with the seven before them. */
export function defaultFilters(now = new Date()): BdmFilters {
  const to = lastCompleteDay(now);
  return {
    from: addDays(to, 1 - DEFAULT_WINDOW_DAYS),
    to,
    comparison: 'Previous Period',
    cadence: 'Weekly',
    attributionWindow: 7,
    split: 'BDM Name',
    bdm: [],
    segments: [],
  };
}

const isDay = (value: string | null): value is string => !!value && dayjs(value, ISO, true).isValid();

export function filtersError(filters: BdmFilters, now = new Date()): string | null {
  if (!isDay(filters.from) || !isDay(filters.to)) {
    return 'Choose valid dates in YYYY-MM-DD format.';
  }
  if (filters.from > filters.to) {
    return 'The start date must be on or before the end date.';
  }
  if (filters.to > lastCompleteDay(now)) {
    return `Data is complete to ${lastCompleteDay(now)}. Choose an earlier end date.`;
  }
  return null;
}

/**
 * The workbook's comparison windows ("Date Custom Range (Previous) TF"): the same number of days
 * ending the day before the start, or the selected range moved back a month, quarter or year.
 */
export function comparisonRange(filters: Pick<BdmFilters, 'from' | 'to' | 'comparison'>): { from: string; to: string } {
  const length = dayjs(filters.to).diff(dayjs(filters.from), 'day');
  if (filters.comparison === 'Previous Period') {
    return { from: addDays(filters.from, -(length + 1)), to: addDays(filters.from, -1) };
  }
  const back =
    filters.comparison === 'Previous Month'
      ? dayjs(filters.from).subtract(1, 'month')
      : filters.comparison === 'Previous Quarter'
        ? dayjs(filters.from).subtract(3, 'month')
        : dayjs(filters.from).subtract(1, 'year');
  return { from: back.format(ISO), to: back.add(length, 'day').format(ISO) };
}

const pick = <T extends string | number>(values: ReadonlyArray<T>, raw: string | null, fallback: T): T =>
  values.find((value) => String(value) === raw) ?? fallback;

const list = (raw: string | null): Array<string> | null => {
  if (!raw) {
    return null;
  }
  try {
    const value: unknown = JSON.parse(raw);
    return Array.isArray(value) && value.every((item) => typeof item === 'string') ? value : null;
  } catch {
    return null;
  }
};

export function parseFilters(params: URLSearchParams, now = new Date()): BdmFilters {
  const filters = defaultFilters(now);
  return {
    from: isDay(params.get('from')) ? params.get('from') : filters.from,
    to: isDay(params.get('to')) ? params.get('to') : filters.to,
    comparison: pick(COMPARISONS, params.get('comparison'), filters.comparison),
    cadence: pick(CADENCES, params.get('cadence'), filters.cadence),
    attributionWindow: pick(ATTRIBUTION_WINDOWS, params.get('attributionWindow'), filters.attributionWindow),
    split: pick(SPLITS, params.get('split'), filters.split),
    bdm: list(params.get('bdm')) ?? filters.bdm,
    segments: list(params.get('segments')) ?? filters.segments,
  };
}

export function writeFilters(filters: BdmFilters, current: URLSearchParams, now = new Date()): URLSearchParams {
  const params = new URLSearchParams(current);
  const defaults = defaultFilters(now);
  for (const key of ['from', 'to', 'comparison', 'cadence', 'attributionWindow', 'split'] as const) {
    if (filters[key] === defaults[key]) {
      params.delete(key);
    } else {
      params.set(key, String(filters[key]));
    }
  }
  for (const key of ['bdm', 'segments'] as const) {
    if (filters[key].length) {
      params.set(key, JSON.stringify([...new Set(filters[key])].sort()));
    } else {
      params.delete(key);
    }
  }
  return params;
}

export function lensFilters(filters: BdmFilters): LensFilterValues {
  const values: LensFilterValues = {
    period: { window: { from: filters.from, to: filters.to }, comparison: comparisonRange(filters) },
    cadence: filters.cadence,
  };
  if (filters.bdm.length) {
    values.bdm_name = filters.bdm;
  }
  if (filters.segments.length) {
    values.coverage_segment = filters.segments;
  }
  return values;
}

const num = (value: unknown): number | null => {
  if (value === null || value === undefined || value === '') {
    return null;
  }
  const parsed = Number(typeof value === 'object' && value && 'value' in value ? value.value : value);
  return Number.isFinite(parsed) ? parsed : null;
};
/** A row value as a label; BigQuery DATE and NUMERIC values may arrive wrapped as { value }. */
export function text(value: unknown): string {
  const raw = typeof value === 'object' && value !== null && 'value' in value ? value.value : value;
  return typeof raw === 'string' || typeof raw === 'number' || typeof raw === 'boolean' ? String(raw) : 'Unknown';
}
const divide = (a: number | null, b: number | null) => (a === null || b === null || b === 0 ? null : a / b);

export function readCard(cards: ReadonlyArray<LensCardData>, cardId: string): CardResult | CardProblem {
  const card = cards.find((item) => item.cardId === cardId);
  if (!card) {
    return { problem: 'This section is still loading.' };
  }
  if (card.status === 'ready' || card.status === 'empty') {
    return { rows: card.rows ?? [], truncated: Boolean(card.truncated), card };
  }
  if (card.status === 'restricted') {
    return { problem: card.message ?? 'You do not have access to this section.' };
  }
  return { problem: `This section could not load. ${card.message ?? ''}`.trim() };
}
export const isProblem = (result: CardResult | CardProblem): result is CardProblem => 'problem' in result;

type KpiKey = 'bookings' | 'interactions' | 'bookingsPer100' | 'converted' | 'newAgentShare';
export type Kpi = {
  key: KpiKey;
  label: string;
  current: number | null;
  previous: number | null;
  change: number | null;
};

const per100 = (bookings: number | null, interactions: number | null) => {
  const ratio = divide(bookings, interactions);
  return ratio === null ? null : ratio * 100;
};

const convertedColumn = (window: AttributionWindow) => `converted_agencies_${window}d`;

function kpiValues(
  totals: Record<string, unknown> | undefined,
  agencies: Record<string, unknown> | undefined,
  window: AttributionWindow,
) {
  const bookings = num(totals?.agency_booking_count);
  const interactions = num(totals?.interaction_count);
  return {
    bookings,
    interactions,
    bookingsPer100: per100(bookings, interactions),
    converted: divide(num(agencies?.[convertedColumn(window)]), num(agencies?.interacted_agencies)),
    newAgentShare: divide(num(totals?.new_agent_booking_count), bookings),
  };
}

const changeOf = (current: number | null, previous: number | null) => {
  const ratio = divide(current, previous);
  return ratio === null ? null : ratio - 1;
};

const KPI_LABELS: Record<KpiKey, string> = {
  bookings: 'Agency Booking Count',
  interactions: 'Interaction Count',
  bookingsPer100: 'Bookings per 100 Interactions',
  converted: '% Agencies Converted',
  newAgentShare: '% New Agent Bookings',
};

/** The five KPI tiles. Ratios divide period totals, and % change is current / previous - 1, as in the workbook. */
export function kpisOf(totals: CardResult, agencies: CardResult, window: AttributionWindow): Array<Kpi> {
  const current = kpiValues(totals.rows[0], agencies.rows[0], window);
  const previous = kpiValues(totals.card.comparison?.rows?.[0], agencies.card.comparison?.rows?.[0], window);
  return (Object.keys(KPI_LABELS) as Array<KpiKey>).map((key) => ({
    key,
    label: KPI_LABELS[key],
    current: current[key],
    previous: previous[key],
    change: changeOf(current[key], previous[key]),
  }));
}

export type Pane = { title: string; format: 'number' | 'percent' | 'decimal'; stacked: boolean; series: Array<Series> };
type Series = { name: string; values: Array<number | null> };
export type ChartData = { categories: Array<string>; panes: Array<Pane> };

const periodsOf = (rows: Array<Record<string, unknown>>, key: string) =>
  [...new Set(rows.map((row) => text(row[key])))].sort();

/** Sums `measure` per period for each value of `seriesKey`; largest series first unless `order` is given. */
function splitSeries(
  rows: Array<Record<string, unknown>>,
  periodKey: string,
  seriesKey: string,
  measure: string,
  categories: Array<string>,
  order?: ReadonlyArray<string>,
): Array<Series> {
  const totals = new Map<string, Map<string, number>>();
  for (const row of rows) {
    const name = text(row[seriesKey]);
    const byPeriod = totals.get(name) ?? new Map<string, number>();
    byPeriod.set(text(row[periodKey]), (byPeriod.get(text(row[periodKey])) ?? 0) + (num(row[measure]) ?? 0));
    totals.set(name, byPeriod);
  }
  const weight = (name: string) => [...(totals.get(name)?.values() ?? [])].reduce((sum, value) => sum + value, 0);
  const names = [...totals.keys()].sort((a, b) =>
    order ? order.indexOf(a) - order.indexOf(b) || a.localeCompare(b) : weight(b) - weight(a) || a.localeCompare(b),
  );
  return names.map((name) => ({
    name,
    values: categories.map((period) => totals.get(name)?.get(period) ?? null),
  }));
}

/** Interactions split by BDM or by whether the agency booked; bookings stay one total, as in the workbook. */
export function effortData(result: CardResult, split: Split): ChartData {
  const seriesKey = split === 'BDM Name' ? 'bdm_name' : 'agency_booked_in_period';
  const categories = periodsOf(result.rows, 'period_start');
  const bookings = new Map<string, number>();
  for (const row of result.rows) {
    const period = text(row.period_start);
    bookings.set(period, (bookings.get(period) ?? 0) + (num(row.agency_booking_count) ?? 0));
  }
  return {
    categories,
    panes: [
      {
        title: 'Interactions',
        format: 'number',
        stacked: true,
        series: splitSeries(result.rows, 'period_start', seriesKey, 'interaction_count', categories),
      },
      {
        title: 'Agency bookings',
        format: 'number',
        stacked: false,
        series: [{ name: 'Agency bookings', values: categories.map((period) => bookings.get(period) ?? null) }],
      },
    ],
  };
}

export function conversionData(totals: CardResult, agencies: CardResult, window: AttributionWindow): ChartData {
  const categories = [
    ...new Set([...periodsOf(totals.rows, 'period_start'), ...periodsOf(agencies.rows, 'period_start')]),
  ].sort();
  const byPeriod = (rows: Array<Record<string, unknown>>) => new Map(rows.map((row) => [text(row.period_start), row]));
  const t = byPeriod(totals.rows);
  const a = byPeriod(agencies.rows);
  return {
    categories,
    panes: [
      {
        title: 'Bookings per 100 interactions',
        format: 'decimal',
        stacked: false,
        series: [
          {
            name: 'Bookings per 100 interactions',
            values: categories.map((period) =>
              per100(num(t.get(period)?.agency_booking_count), num(t.get(period)?.interaction_count)),
            ),
          },
        ],
      },
      {
        title: `% agencies converted (${window} days)`,
        format: 'percent',
        stacked: false,
        series: [
          {
            name: `% agencies converted (${window} days)`,
            values: categories.map((period) =>
              divide(num(a.get(period)?.[convertedColumn(window)]), num(a.get(period)?.interacted_agencies)),
            ),
          },
        ],
      },
    ],
  };
}

/** Tableau's percent of total, computed per period. */
export function newVsExistingData(result: CardResult): ChartData {
  const categories = periodsOf(result.rows, 'period_start');
  const counts = splitSeries(result.rows, 'period_start', 'new_agent_flag', 'agency_booking_count', categories, [
    'New Agent',
    'Existing',
  ]);
  const totals = categories.map((_, index) => counts.reduce((sum, series) => sum + (series.values[index] ?? 0), 0));
  return {
    categories,
    panes: [
      {
        title: '% of agency bookings',
        format: 'percent',
        stacked: true,
        series: counts.map((series) => ({
          name: series.name,
          values: series.values.map((value, index) => divide(value ?? 0, totals[index])),
        })),
      },
    ],
  };
}

export function agencyStatusData(result: CardResult): ChartData {
  const categories = periodsOf(result.rows, 'period_start');
  return {
    categories,
    panes: [
      {
        title: 'Agencies',
        format: 'number',
        stacked: true,
        series: splitSeries(result.rows, 'period_start', 'coverage_segment', 'agencies', categories, COVERAGE_SEGMENTS),
      },
    ],
  };
}

const BANDS = ['0', '1', '2', '3+'];

export function cohortData(result: CardResult, periodKey: 'signup_period' | 'first_booking_period'): ChartData {
  const categories = periodsOf(result.rows, periodKey);
  const bookings = new Map<string, number>();
  for (const row of result.rows) {
    bookings.set(text(row[periodKey]), (bookings.get(text(row[periodKey])) ?? 0) + (num(row.total_bookings) ?? 0));
  }
  return {
    categories,
    panes: [
      {
        title: 'Agencies',
        format: 'number',
        stacked: true,
        series: splitSeries(result.rows, periodKey, 'booking_count_band', 'agency_count', categories, BANDS),
      },
      {
        title: 'Total bookings',
        format: 'number',
        stacked: false,
        series: [
          {
            name: 'Total bookings',
            values: categories.map((period) => bookings.get(period) ?? null),
          },
        ],
      },
    ],
  };
}

/** Period labels as the workbook prints them: "1 Sep 25 w/c", "Sep 25", "Q3 25", "2025". */
export function periodLabel(period: string, cadence: Cadence): string {
  const day = dayjs(period, ISO, true);
  if (!day.isValid()) {
    return period;
  }
  switch (cadence) {
    case 'Daily':
      return day.format('D MMM YY');
    case 'Weekly':
      return `${day.format('D MMM YY')} w/c`;
    case 'Monthly':
      return day.format('MMM YY');
    case 'Quarterly':
      return `Q${Math.floor(day.month() / 3) + 1} ${day.format('YY')}`;
    default:
      return day.format('YYYY');
  }
}

export function formatValue(value: number | null, format: Pane['format']): string {
  if (value === null) {
    return '–';
  }
  if (format === 'percent') {
    return `${(value * 100).toFixed(1)}%`;
  }
  if (format === 'decimal') {
    return value.toFixed(1);
  }
  return Math.round(value).toLocaleString('en-AU');
}

export function changeText(change: number | null): string {
  if (change === null) {
    return 'No comparison';
  }
  const sign = change > 0 ? '▲ +' : change < 0 ? '▼ ' : '► ';
  return `${sign}${(change * 100).toFixed(1)}%`;
}

export function chartCsv(title: string, data: ChartData, cadence: Cadence): string {
  const escape = (value: string) => {
    const safe = /^[=+\-@]/.test(value) ? `'${value}` : value;
    return /[",\n]/.test(safe) ? `"${safe.replace(/"/g, '""')}"` : safe;
  };
  const lines = [['Chart', 'Measure', 'Series', 'Period start', 'Period', 'Value'].join(',')];
  for (const pane of data.panes) {
    for (const series of pane.series) {
      data.categories.forEach((period, index) => {
        lines.push(
          [title, pane.title, series.name, period, periodLabel(period, cadence), String(series.values[index] ?? '')]
            .map(escape)
            .join(','),
        );
      });
    }
  }
  return `${lines.join('\n')}\n`;
}
